import type { UpdateApproverResponsesMutation } from '@risksmart-app/web-graphql-client/generated/graphql';
import { UpdateApproverResponsesDocument } from '@risksmart-app/web-graphql-client/generated/graphql';

import { createMutationHook } from '@/utils/hook-factories';

/**
 * Caller-facing input — the active approval LEVEL the actor is responding to.
 * The actor's approver-slot resolution happens SERVER-SIDE (tRPC service /
 * legacy Hasura action), never in the component, so callers pass only the
 * `levelId`.
 */
export interface SubmitApproverResponseInput {
  changeRequestId: string;
  parentId: string;
  levelId: string;
  approved: boolean;
  comment: string;
}

/**
 * Approver-response submit (RSP-6087). Both paths take the `levelId` and
 * resolve the actor's eligible approver slot(s) server-side: `trpc` on →
 * tRPC `changeRequest.recordApproverResponse`; off → the legacy Apollo
 * `updateApproverResponses` action. Errors reject (no toast here) — the
 * calling panel surfaces them.
 *
 * Outcomes on the tRPC path: an actor with NO eligible slot at the level (or
 * whose slots are already approved — approvals are final) is a successful
 * NO-OP (`affected_rows: 0`), matching the legacy action; `mutateAsync` only
 * rejects on real failures (stale/non-Pending CR, forged slot claims the
 * data-layer 403s, validation errors). Don't build UI affordances that
 * expect a rejection for mere ineligibility.
 */
export const useUpsertApproverResponses = createMutationHook({
  name: 'submitApproverResponse',
  mutationOptions: (trpc, onSuccess) =>
    trpc.frontend.changeRequest.recordApproverResponse.mutationOptions({
      onSuccess,
    }),
  mapInput: (input: SubmitApproverResponseInput) => input,
  // Mirror the Apollo path's evictField(cache, 'change_request_by_pk') —
  // refresh the per-parent CR feeds so the recorded response + any status
  // flip surface without a manual reload.
  invalidate: (trpc) => [
    trpc.frontend.changeRequest.changeRequestsByParent.queryKey(),
    trpc.frontend.changeRequest.pendingChangeRequests.queryKey(),
    // COI-889: `/requests` reads `RequiresAction` off the register.
    trpc.frontend.changeRequest.register.queryKey(),
    // COI-889: the my-items ribbon counts this entity.
    trpc.frontend.myItems.pathKey(),
  ],
  // Consumers ignore the payload — echo the CR id.
  mapResponse: (
    _result,
    input: SubmitApproverResponseInput
  ): UpdateApproverResponsesMutation => ({
    updateApproverResponses: {
      __typename: 'IdOutput',
      Id: input.changeRequestId,
    },
  }),
  graphqlDocument: UpdateApproverResponsesDocument,
  graphqlVariables: (input: SubmitApproverResponseInput) => ({
    input: {
      ChangeRequestId: input.changeRequestId,
      Response: input.approved,
      Comment: input.comment,
      OverrideLevel: false,
      LevelId: input.levelId,
    },
  }),
  evictFields: ['change_request_by_pk'],
});
